import { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { FormQuestions, FormUser, FormQuestion } from '@amzn/coral_com-amazon-asxcustomerprofileservice';
import { useBundle } from '@amzn/react-arb-tools';
import { ApiErrorBanner, ICON_NAME } from '@amzn/sss-website-theme';

import { AcpsCreditsClientContext } from 'src/clients';
import {
  isRebrandingWeblabEnabled,
  isStorefrontListingEnabled,
  isUkLaunchEnabled,
} from 'src/clients/weblab/weblab-client';
import {
  OrganizationCommitmentsFormValues,
  OrganizationInformationFormValues,
  INTAKE_FORM_KEYS,
} from 'src/components/pages/intake-form/FormEnums';
import { OrganizationCommitmentsForm } from 'src/components/pages/intake-form/forms/OrganizationCommitmentsForm';
import { useIntakeForm } from 'src/components/pages/intake-form/pages/IntakeFormContext';
import { ThankYouPage } from 'src/components/pages/intake-form/thank-you/ThankYouPage';
import {
  INTAKE_FORM_PAGE_BUNDLE_NAME,
  FormStrings,
  INFORMATION_FORM_STORAGE,
  IntakeFormQuestionInfo,
} from 'src/constants';
import { ROUTES } from 'src/enums/Routes';
import { t } from 'src/helpers';
import { useGetUserRights } from 'src/hooks';
import { useUnifiedErrorHandler } from 'src/hooks/useUnifiedErrorHandler';

import { IntakeFormTemplate } from './IntakeFormTemplate';

export const CommitmentsFormPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useGetUserRights();
  const [formBundle] = useBundle(INTAKE_FORM_PAGE_BUNDLE_NAME);
  const intakeFormData = useIntakeForm();
  const acpsClient = useContext(AcpsCreditsClientContext);

  // Track submission success
  const [isSubmittedSuccessfully, setIsSubmittedSuccessfully] = useState(false);
  const [submittedCountryCode, setSubmittedCountryCode] = useState('');

  const { errorState, handleError, clearErrors } = useUnifiedErrorHandler({
    onServerError: () => {
      navigate(ROUTES.ERROR_PAGE, { replace: true });
    },
  });

  const handleSubmit = async (step2Data: OrganizationCommitmentsFormValues) => {
    clearErrors();

    const step1CachedData = localStorage.getItem(INFORMATION_FORM_STORAGE);
    const formSubmitted = Boolean(intakeFormData?.partialUser && intakeFormData?.partialQuestions);

    if (!(step1CachedData || formSubmitted)) {
      navigate(ROUTES.INTAKE_PAGE_STEP_1, { replace: true });
      return;
    }

    let step1DataFromStorage: OrganizationInformationFormValues | undefined;
    if (step1CachedData) {
      try {
        step1DataFromStorage = JSON.parse(step1CachedData);
      } catch {
        step1DataFromStorage = undefined;
      }
    }

    const contextUser = intakeFormData?.partialUser;

    const combined = {
      ...(step1DataFromStorage ?? {}),
      ...step2Data,
      completedStep: 2,
    };

    try {
      const user: FormUser = contextUser ?? {
        name: (combined as any)[INTAKE_FORM_KEYS.NAME],
        email: (combined as any)[INTAKE_FORM_KEYS.EMAIL],
        company: (combined as any)[INTAKE_FORM_KEYS.ORGANIZATION_NAME],
        relationToAmazon: {
          selectedRelationsToAmazon: combined[INTAKE_FORM_KEYS.ORGANIZATION_RELATIONSHIP] ?? [],
        },
        countryCode: (combined as any)[INTAKE_FORM_KEYS.COUNTRY_CODE] ?? '',
      };

      const rawNetZero = combined[INTAKE_FORM_KEYS.NET_ZERO_CARBON_TARGET_CRITERIA_QUESTION];
      const rawSub1 = combined[INTAKE_FORM_KEYS.SUBQUESTION_1];

      const hasNetZeroSub =
        typeof rawNetZero === 'string' && rawNetZero.toLowerCase() === 'yes' && rawSub1 && rawSub1.trim() !== '';

      const questions: FormQuestions = [
        FormQuestion.clone({
          questionId: IntakeFormQuestionInfo.NET_ZERO_CARBON_TARGET_CRITERIA_QUESTION.id,
          questionResponse: rawNetZero,
          questionText: IntakeFormQuestionInfo.NET_ZERO_CARBON_TARGET_CRITERIA_QUESTION.text,
          ...(hasNetZeroSub && {
            subquestions: [
              FormQuestion.clone({
                questionId: IntakeFormQuestionInfo.SUBQUESTION_1.id,
                questionResponse: rawSub1,
                questionText: IntakeFormQuestionInfo.SUBQUESTION_1.text,
              }),
            ],
          }),
        }),
        FormQuestion.clone({
          questionId: IntakeFormQuestionInfo.MEASURE_AND_REPORT_CRITERIA_QUESTION.id,
          questionResponse: (combined as any)[INTAKE_FORM_KEYS.MEASURE_AND_REPORT_CRITERIA_QUESTION],
          questionText: IntakeFormQuestionInfo.MEASURE_AND_REPORT_CRITERIA_QUESTION.text,
        }),
      ];

      if (!acpsClient) {
        console.error('ACPS client not available');
        navigate(ROUTES.ERROR_PAGE, { replace: true });

        return;
      }

      if (!isAuthenticated) {
        await acpsClient.saveCreditsInterestForm(user, questions);
        localStorage.removeItem(INFORMATION_FORM_STORAGE);
        setSubmittedCountryCode(user.countryCode ?? '');
        setIsSubmittedSuccessfully(true);
      }

      if (isAuthenticated) {
        await acpsClient.upsertCreditsInterestForm(user, questions);
        localStorage.removeItem(INFORMATION_FORM_STORAGE);
        setSubmittedCountryCode(user.countryCode ?? '');
        setIsSubmittedSuccessfully(true);
      }
    } catch (err) {
      handleError(err, 'commitments-form-page');
    }
  };

  if (isSubmittedSuccessfully) {
    const storefrontRoute = isStorefrontListingEnabled ? ROUTES.CREDITS_STORE : ROUTES.CREDITS_STOREFRONT;
    const isSupportedCountry =
      submittedCountryCode === 'US' || submittedCountryCode === 'GB' || submittedCountryCode === 'UK';

    if (isUkLaunchEnabled) {
      // Case 1: User is authenticated and has selected US or UK for country
      if (isAuthenticated && isSupportedCountry) {
        return (
          <ThankYouPage
            toastTitle={t(formBundle, FormStrings.ELIGIBLE_AUTHENTICATED_TOAST_TITLE)}
            toastBody={t(formBundle, FormStrings.ELIGIBLE_AUTHENTICATED_TOAST_BODY)}
            title={t(formBundle, FormStrings.ELIGIBLE_AUTHENTICATED_TITLE)}
            copy=""
            cta={t(formBundle, FormStrings.ELIGIBLE_AUTHENTICATED_CTA)}
            ctaSrc={storefrontRoute}
            forceServerSideNav
          />
        );
      }

      // Case 2: User is NOT authenticated and has selected US or UK for country
      if (!isAuthenticated && isSupportedCountry) {
        return (
          <ThankYouPage
            toastTitle={t(formBundle, FormStrings.ELIGIBLE_UNAUTHENTICATED_TOAST_TITLE)}
            toastBody={t(formBundle, FormStrings.ELIGIBLE_UNAUTHENTICATED_TOAST_BODY)}
            title={t(formBundle, FormStrings.ELIGIBLE_UNAUTHENTICATED_TITLE)}
            copy=""
            cta={t(formBundle, FormStrings.ELIGIBLE_UNAUTHENTICATED_CTA)}
            ctaSrc={storefrontRoute}
            forceServerSideNav
          />
        );
      }

      // Case 3: User is authenticated and has selected a country that's not US or UK
      if (isAuthenticated && !isSupportedCountry) {
        return (
          <ThankYouPage
            toastTitle=""
            toastBody=""
            title={t(formBundle, FormStrings.INELIGIBLE_AUTHENTICATED_TITLE)}
            copy={t(formBundle, FormStrings.INELIGIBLE_AUTHENTICATED_COPY)}
            cta={t(formBundle, FormStrings.INELIGIBLE_AUTHENTICATED_CTA)}
            ctaSrc={ROUTES.SUSTAINABILITY_LANDING}
            forceServerSideNav
          />
        );
      }

      // Case 4: User is NOT authenticated and has selected a country that's not US or UK
      if (!isAuthenticated && !isSupportedCountry) {
        return (
          <ThankYouPage
            toastTitle=""
            toastBody=""
            title={t(formBundle, FormStrings.INELIGIBLE_UNAUTHENTICATED_TITLE)}
            copy={t(formBundle, FormStrings.INELIGIBLE_UNAUTHENTICATED_COPY)}
            cta={t(formBundle, FormStrings.INELIGIBLE_UNAUTHENTICATED_CTA)}
            ctaSrc={ROUTES.MY_ACCOUNT_PAGE}
            forceServerSideNav
          />
        );
      }
    }

    // Case 5: Fallback if none of the above conditions are satisfied
    return (
      <ThankYouPage
        toastTitle=""
        toastBody=""
        title={t(formBundle, FormStrings.FALLBACK_UNQUALIFIED_TITLE)}
        copy={t(formBundle, FormStrings.FALLBACK_UNQUALIFIED_COPY)}
        cta={t(formBundle, FormStrings.FALLBACK_UNQUALIFIED_CTA)}
        ctaSrc={ROUTES.SUSTAINABILITY_LANDING}
        forceServerSideNav
      />
    );
  }

  return (
    <IntakeFormTemplate activeStep={1}>
      {errorState.hasErrors && (
        <ApiErrorBanner
          errors={errorState.errors}
          errorMessages={{
            generic: t(formBundle, FormStrings.ERRORS_PRESENT_DESCRIPTION),
          }}
          iconName={ICON_NAME.INFO_FILLED}
          testId="api-error-banner"
        />
      )}
      <OrganizationCommitmentsForm
        isRebrandingActive={isRebrandingWeblabEnabled}
        handleFormSubmit={handleSubmit}
        intakeFormBundle={formBundle}
        backCtaLabel={t(formBundle, FormStrings.BACK)}
        ctaLabel={t(formBundle, FormStrings.FORM_SUBMIT_BUTTON_LABEL)}
      />
    </IntakeFormTemplate>
  );
};
